"use client";
import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader, StatCard } from "../ModuleHelpers";
import { useERP, formatBDT, formatNum } from "@/lib/erp/store";
import { Brain, Send, Sparkles } from "lucide-react";

export function AICopilotModule() {
  const erp = useERP();
  const [msgs, setMsgs] = React.useState<{ from: "user" | "ai"; text: string }[]>([
    { from: "ai", text: "Hi! I'm your OMEGA AI Copilot. Ask me about sales, inventory, finance or suppliers." },
  ]);
  const [input, setInput] = React.useState("");

  const ask = (q: string) => {
    if (!q.trim()) return;
    setMsgs(m => [...m, { from: "user", text: q }]);
    setInput("");
    setTimeout(() => {
      let ans = "";
      const lc = q.toLowerCase();
      if (lc.includes("stock")) {
        const lowStock = erp.products.filter(p => erp.getStock(p.id) < p.reorderPoint).length;
        ans = `${lowStock} products are below reorder point. Recommend immediate PO for those.`;
      } else if (lc.includes("outstanding") || lc.includes("ar")) {
        ans = `Total outstanding AR is ${formatBDT(erp.customers.reduce((s, c) => s + c.outstanding, 0))} across ${erp.customers.filter(c => c.outstanding > 0).length} customers.`;
      } else if (lc.includes("sales")) {
        ans = `Net sales this period: ${formatBDT(erp.salesInvoices.reduce((s, i) => s + i.amount, 0))} from ${erp.salesInvoices.length} invoices.`;
      } else if (lc.includes("supplier")) {
        ans = `You have ${erp.suppliers.length} suppliers. Top rated: ${erp.suppliers.slice().sort((a, b) => b.rating - a.rating)[0]?.name}.`;
      } else {
        ans = "I can help with stock, AR, sales, suppliers, margins, and PO approvals. Try asking 'Show low stock items'.";
      }
      setMsgs(m => [...m, { from: "ai", text: ans }]);
    }, 700);
  };

  const suggestions = ["Show low stock items", "Total outstanding AR", "Net sales this period", "Top supplier"];

  return (
    <div className="flex flex-col gap-6">
      <PageHeader title="AI Copilot" desc="Conversational AI assistant for instant business insights." icon={Brain} />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Queries Today" value={formatNum(msgs.filter(m => m.from === "user").length)} />
        <StatCard label="Avg Response" value="0.7s" tone="text-primary" />
        <StatCard label="Confidence" value="91%" />
        <StatCard label="Auto-Actions" value="3" />
      </div>
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Sparkles className="text-primary size-5" /> Chat with OMEGA Copilot</CardTitle></CardHeader>
        <CardContent>
          <div className="omega-sidebar-scroll mb-3 max-h-80 overflow-y-auto space-y-2 border rounded-md p-3">
            {msgs.map((m, i) => (
              <div key={i} className={`flex ${m.from === "user" ? "justify-end" : ""}`}>
                <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${m.from === "user" ? "bg-primary text-primary-foreground" : "bg-muted"}`}>
                  {m.from === "ai" && <span className="mb-1 flex items-center gap-1 text-xs font-semibold opacity-70"><Brain className="size-3" /> AI</span>}
                  {m.text}
                </div>
              </div>
            ))}
          </div>
          <div className="mb-2 flex flex-wrap gap-1.5">
            {suggestions.map(s => <Button key={s} size="sm" variant="outline" onClick={() => ask(s)}>{s}</Button>)}
          </div>
          <div className="flex gap-2">
            <Input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === "Enter") ask(input); }} placeholder="Ask anything about your business..." />
            <Button onClick={() => ask(input)}><Send className="size-4" /></Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
